import { TransactionListItem } from "@common/schemas/transaction.schema";
import { useMemo } from "react";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { toDisplayDateOnly } from "@helpers/date";
import { useMatchTransfer, useUnmatchTransfer } from "@mutations/transaction";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";

interface MatchButtonProps {
    matchedTransaction: TransactionListItem | null;
    originTransaction: TransactionListItem;
    targetTransaction: TransactionListItem;
    onMatch: (
        originTransaction: TransactionListItem,
        targetTransaction: TransactionListItem,
    ) => void;
    onUnmatch: () => void;
}
export const MatchButton: React.FC<MatchButtonProps> = ({
    matchedTransaction,
    originTransaction,
    targetTransaction,
    onUnmatch,
    onMatch,
}: MatchButtonProps) => {
    const unmatchTransaction = useUnmatchTransfer({
        financialTransaction: originTransaction,
        onSuccess: () => {
            onUnmatch();
        },
    });

    const matchTransaction = useMatchTransfer({
        originTransaction,
        targetTransaction,
        onSuccess: ({
            originTransaction: originTransactionUpdated,
            targetTransaction: targetTransactionUpdated,
        }) => {
            onMatch(
                // NOTE: merge new and previous states to keep eager loaded entities
                // consistency with transactions table
                { ...originTransaction, ...originTransactionUpdated },
                { ...targetTransaction, ...targetTransactionUpdated },
            );
        },
    });

    const isLockedOriginTransaction = useIsDateLocked(
        originTransaction.entity.id,
        toDisplayDateOnly(originTransaction.dateOnly ?? originTransaction.date),
    );
    const isLockedTargetTransaction = useIsDateLocked(
        targetTransaction.entity.id,
        toDisplayDateOnly(targetTransaction.dateOnly ?? targetTransaction.date),
    );

    const buttonLabel = useMemo(
        () =>
            matchedTransaction?.id === targetTransaction.id
                ? "Unmatch"
                : "Match",
        [matchedTransaction, targetTransaction],
    );

    return (
        <ButtonWithLoader
            data-testid="add-agg-filter"
            size="sm"
            variant="primary"
            disabled={isLockedOriginTransaction || isLockedTargetTransaction}
            onClick={async () => {
                if (matchedTransaction?.id === targetTransaction.id) {
                    await unmatchTransaction.mutateAsync();
                    return;
                }
                await matchTransaction.mutateAsync();
            }}
            loading={unmatchTransaction.isPending || matchTransaction.isPending}
        >
            {buttonLabel}
        </ButtonWithLoader>
    );
};
